// sessionCharacter (ENG-907) — the ONE place that answers "which convorcher
// character does this page converse as?".
//
// The rendered character and the conversational character are different things.
// A splat/OGS avatar renders as `isaac-ogs` but is driven by rig params out of
// the OGS splat pipeline and has no persona of its own, so it must always talk through the
// `isaac` brain. It declares that in its Event Graph, on a `convorcherSession`
// node (public/blueprints/isaac.json → `act_convorcher_session`).
//
// Before this module there were three writers pushing a character at
// ConvorcherClient — the constructor's `?character=` URL param, App's
// character-change effect, and the `convorcherSession` node's runtime apply —
// and the first two used the RENDERED slug. The rendered slug won the race,
// because the node only fires on `beginPlay`, which App gates on the avatar
// having finished loading. So every isaac-ogs load opened a session bound to a
// slug with no `brain_characters` row: convorcher answers with text but logs
// `no voice ID configured, dropping message`, so the reply carries zero audio
// bytes and zero ARKit frames. Measured windows in prod logs ran from 1s to
// 12min, and the window is user-reachable (the loading overlay blocks the
// pointer but not Tab focus, so three Tab presses reach the message input).
//
// Making every writer resolve through this function is what removes the
// ordering hazard: whichever runs first computes the SAME config, so the
// rendered slug can never open a session the graph would have overridden, and
// the node's own apply finds its config already in place and no-ops instead of
// discarding a live session.
//
// Pure — no React, no network, no module singletons. Unit-tested in
// tests/unit/sessionCharacter.test.mjs.

const str = (v) => (v == null ? '' : String(v).trim())

/**
 * The graph's `convorcherSession` node, or null.
 *
 * Searches the top level and one level into any `graphGroup` container's
 * `data.innerNodes` — the same one-level recursion EventRuntime.hasNodeOfType
 * and graphGroup.collectGroupStarters use, because `convorcherSession` is a
 * GLOBAL_UNIQUE type (EventGraph.jsx) that is explicitly allowed to live inside
 * a group. Miss the group pool and a group-nested declaration reads as "no
 * session declared".
 *
 * @param {Array<object>?} nodes top-level event-graph nodes
 */
export function findConvorcherSessionNode(nodes) {
  for (const n of nodes ?? []) {
    if (n?.type === 'convorcherSession') return n
    if (n?.type === 'graphGroup') {
      for (const inner of n.data?.innerNodes ?? []) {
        if (inner?.type === 'convorcherSession') return inner
      }
    }
  }
  return null
}

/**
 * Resolve the convorcher session config for a page.
 *
 * @param {object}  args
 * @param {string?} args.renderedSlug the character being RENDERED (character.id)
 * @param {Array?}  args.nodes        live event-graph nodes (eventGraphState.nodes)
 * @returns {{character: string|null, features: string|null, story: string|null,
 *            source: 'graph'|'rendered'}}
 *
 * `features`/`story` normalize empty to null on purpose: ConvorcherClient.connect
 * does `this.featuresOverride ?? pageParams.get('features')`, so '' would pin the
 * override to empty and shadow the page-URL param instead of deferring to it.
 *
 * A `convorcherSession` node with no character declares NOTHING — EventRuntime
 * warns and skips it ("no character set — skipping") — so it falls back to the
 * rendered slug rather than parking the session on whatever seeded the client.
 */
export function resolveSessionConfig({ renderedSlug = null, nodes = [] } = {}) {
  const node = findConvorcherSessionNode(nodes)
  const declared = str(node?.data?.character)
  if (declared) {
    return {
      character: declared,
      features: str(node.data?.features) || null,
      story: str(node.data?.story) || null,
      source: 'graph',
    }
  }
  return { character: str(renderedSlug) || null, features: null, story: null, source: 'rendered' }
}

/**
 * Whether a persisted `session_id` may be resumed for the character we are
 * about to converse as.
 *
 * The id survives reloads so a refresh continues the same conversation, but it
 * is only valid for the persona it was opened under. Sending one that belongs to
 * a different character makes convorcher answer `Session <id> exists but options
 * differ. Starting a new session.` and mint a replacement — a wasted session on
 * every cross-character load. Case-insensitive because convorcher resolves the
 * brain with `LOWER(name) = LOWER(?)`.
 *
 * An id stored with no character (written before this was tracked) is NOT
 * resumable: we can't prove it matches, and guessing is what this ticket is
 * about. Costs one fresh session, once per browser.
 */
export function shouldResumeSession({ storedCharacter = null, character = null } = {}) {
  const stored = str(storedCharacter).toLowerCase()
  const want = str(character).toLowerCase()
  return !!stored && !!want && stored === want
}
